import { type VariantProps, cva } from 'class-variance-authority'
import { XIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Hyperlink } from '@/components/lemmala-design-system/link/hyperlink'

const alertVariants = cva('grid items-start relative w-full rounded p-3 gap-1', {
  variants: {
    variant: {
      default: 'bg-main-background-neutral text-main-foreground-primary',
      info: 'bg-main-background-subtle-semantic-accent text-main-foreground-accent',
      destructive: 'bg-main-background-subtle-semantic-danger text-main-foreground-danger',
      attention: 'bg-main-background-subtle-semantic-attention text-main-foreground-attention',
      warning: 'bg-main-background-subtle-semantic-warning text-main-foreground-warning',
      success: 'bg-main-background-subtle-semantic-success text-main-foreground-success',
    },
    layout: {
      horizontal: 'grid-flow-col auto-cols-[minmax(0,max-content)]',
      vertical: 'grid-cols-[min-content_1fr]',
    },
  },
  defaultVariants: {
    variant: 'default',
    layout: 'horizontal',
  },
})

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    VariantProps<typeof alertVariants> & {
      icon?: React.ReactNode
      linkHref?: string
      linkText?: string
      alertTitle?: string
      description?: React.ReactNode
      /** Shows an X button in the top-right corner. */
      onDismiss?: () => void
      dismissLabel?: string
    }
>(
  (
    {
      className,
      variant,
      layout,
      icon,
      alertTitle,
      description,
      linkHref,
      linkText,
      onDismiss,
      dismissLabel = 'Dismiss',
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        role='alert'
        className={cn(alertVariants({ variant, layout }), onDismiss && 'pr-10', className)}
        {...props}
      >
        {icon && <div className={cn(layout === 'vertical' && 'col-start-1')}>{icon}</div>}
        {alertTitle && (
          <AlertTitle className={cn(layout === 'vertical' && 'col-start-2')}>
            {alertTitle}
          </AlertTitle>
        )}
        {description && (
          <AlertDescription className={cn(layout === 'vertical' && 'col-start-2')}>
            {description}
          </AlertDescription>
        )}
        {linkHref && linkText && (
          <Hyperlink
            href={linkHref}
            className={cn(
              'h-fit underline text-body-default',
              layout === 'vertical' && 'col-start-2',
            )}
          >
            {linkText}
          </Hyperlink>
        )}
        {onDismiss && (
          <Button
            variant='ghost'
            size='icon-sm'
            aria-label={dismissLabel}
            onClick={onDismiss}
            className='absolute right-2 top-2'
          >
            <XIcon className='size-4' />
          </Button>
        )}
      </div>
    )
  },
)
Alert.displayName = 'Alert'

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn('text-heading-xxsmall', className)} {...props} />
  ),
)
AlertTitle.displayName = 'AlertTitle'

const AlertDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('min-w-0 text-body-default', className)} {...props} />
))
AlertDescription.displayName = 'AlertDescription'

interface AlertIconProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: React.ElementType
}

const AlertIcon = React.forwardRef<HTMLDivElement, AlertIconProps>(
  ({ icon: Icon, className, ...props }, ref) => (
    <div ref={ref} className={cn('mt-0.5 flex-shrink-0', className)} {...props}>
      <Icon className='size-4' />
    </div>
  ),
)
AlertIcon.displayName = 'AlertIcon'

export { Alert, AlertIcon }
